import {
  COLOR_SELECT_CHANNEL_MAX,
  COLOR_SELECT_HEX_PATTERN,
  COLOR_SELECT_HUE_MAX,
  COLOR_SELECT_HUE_STOPS,
} from './color-select.constants';
import { HsvColor, RgbColor } from './color-select.types';

/** Normalised `#rrggbb`, or null when the text is not a 3- or 6-digit hex color. */
export function normalizeHexColor(
  value: string | null | undefined
): string | null {
  const match = COLOR_SELECT_HEX_PATTERN.exec((value ?? '').trim());
  if (!match) {
    return null;
  }
  const digits =
    match[1].length === 3
      ? match[1]
          .split('')
          .map((digit) => digit + digit)
          .join('')
      : match[1];
  return `#${digits.toLowerCase()}`;
}

export function hexToHsv(hex: string): HsvColor {
  const normalized = normalizeHexColor(hex) ?? '#000000';
  const [r, g, b] = [1, 3, 5].map(
    (start) => parseInt(normalized.slice(start, start + 2), 16) / 255
  );
  const max = Math.max(r, g, b);
  const delta = max - Math.min(r, g, b);

  let h = 0;
  if (delta) {
    if (max === r) {
      h = ((g - b) / delta) % 6;
    } else if (max === g) {
      h = (b - r) / delta + 2;
    } else {
      h = (r - g) / delta + 4;
    }
  }

  return {
    h: (h * 60 + COLOR_SELECT_HUE_MAX) % COLOR_SELECT_HUE_MAX,
    s: max ? delta / max : 0,
    v: max,
  };
}

export function hsvToHex({ h, s, v }: HsvColor): string {
  const channel = (n: number): string => {
    const k = (n + h / 60) % 6;
    const value = v - v * s * Math.max(0, Math.min(k, 4 - k, 1));
    return Math.round(value * 255)
      .toString(16)
      .padStart(2, '0');
  };
  return `#${channel(5)}${channel(3)}${channel(1)}`;
}

/** CSS gradient through every hue, for the hue slider track. */
export function hueSpectrumGradient(): string {
  const stops = Array.from({ length: COLOR_SELECT_HUE_STOPS + 1 }, (_, i) =>
    hsvToHex({
      h: (i * COLOR_SELECT_HUE_MAX) / COLOR_SELECT_HUE_STOPS,
      s: 1,
      v: 1,
    })
  );
  return `linear-gradient(to right, ${stops.join(', ')})`;
}

export function hexToRgb(hex: string): RgbColor {
  const normalized = normalizeHexColor(hex) ?? '#000000';
  return [1, 3, 5].map((start) =>
    parseInt(normalized.slice(start, start + 2), 16)
  ) as RgbColor;
}

/** Channels are rounded and kept within 0-255. */
export function rgbToHex(rgb: RgbColor): string {
  return `#${rgb
    .map((channel) =>
      Math.round(Math.min(COLOR_SELECT_CHANNEL_MAX, Math.max(0, channel)))
        .toString(16)
        .padStart(2, '0')
    )
    .join('')}`;
}

/** Fraction of `size` that `offset` covers, kept within 0-1. */
export function clampFraction(offset: number, size: number): number {
  return size > 0 ? Math.min(1, Math.max(0, offset / size)) : 0;
}
